import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const DEFAULT_CONFIG = {
  entity_name: "Grit Squared Inc.",
  dba_name: "Throughline Housing",
  contact_email: "throughlinehousing@gmail.com",
  business_address: "205 S Main St, Cibolo, TX 78108",
  address_street: "205 S Main St",
  address_locality: "Cibolo",
  address_region: "TX",
  address_postal_code: "78108",
  address_country: "US",
  show_trec: false,
  trec_iabs_url: "",
  trec_cpn_url: "",
  trec_broker_name: "",
  trec_license_number: "",
  current_year: new Date().getUTCFullYear(),
};

let _cache = null;

async function loadConfig() {
  if (_cache) return _cache;
  try {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/config`);
    if (r.ok) _cache = { ...DEFAULT_CONFIG, ...(await r.json()) };
    else _cache = DEFAULT_CONFIG;
  } catch { _cache = DEFAULT_CONFIG; }
  return _cache;
}

/**
 * PublicFooter — legal and positioning footer for every public page.
 * Renders DBA disclosure, dynamic year, positioning statement, policy links,
 * TREC block (config-driven, default hidden), and configurable contact.
 */
export default function PublicFooter({ lang = "en" }) {
  const [cfg, setCfg] = useState(DEFAULT_CONFIG);
  useEffect(() => { loadConfig().then(setCfg); }, []);

  const t = lang === "es" ? {
    privacy: "Privacidad", terms: "Términos", accessibility: "Accesibilidad",
    find: "Encontrar ayuda", provider: "Para propietarios",
    positioning: "Throughline provee información sobre programas de vivienda, propiedades, operación, modelado financiero y desempeño. No provee servicios de corretaje inmobiliario, asesoría de inversión ni asesoría legal; no determina elegibilidad, idoneidad ni acreditación; no garantiza resultados; y no acepta, custodia ni transacciona fondos. Cualquier transacción inmobiliaria que surja de la información aquí encontrada es un compromiso separado con un profesional autorizado.",
    compliance: "Throughline no es una solicitud, ni una lista de espera, ni una entrada. No está afiliada con HUD, VA, ni el condado de Bexar. Cada intake real ocurre a través de Coordinated Entry, 2-1-1, o la agencia nombrada.",
    securities: "Nada en este sitio es una oferta de venta ni una solicitud de compra de valores. Cualquier discusión de inversión es ilustrativa y no constituye asesoría de inversión.",
    contact: "Contacto",
    rights: "Todos los derechos reservados.",
    dba: `${cfg.dba_name} is a d/b/a of ${cfg.entity_name.replace(/\.$/, "")}.`,
    copyright: `© ${cfg.current_year} ${cfg.entity_name.replace(/\.$/, "")}. Todos los derechos reservados.`,
  } : {
    privacy: "Privacy", terms: "Terms", accessibility: "Accessibility",
    find: "Find housing help", provider: "For property owners",
    positioning: "Throughline provides housing program, property, operating, financial-modeling and performance information. It does not provide real estate brokerage services, investment advice, or legal advice; does not determine eligibility, suitability or accreditation; does not guarantee results; and does not accept, custody or transact funds. Any real estate transaction arising from information found here is a separate engagement with a licensed professional.",
    compliance: "Throughline is not an application, waitlist, or intake. It is not affiliated with HUD, VA, or Bexar County. Every intake happens through Coordinated Entry, 2-1-1, or the named agency.",
    securities: "Nothing on this site is an offer to sell or a solicitation to buy any security. Any investment discussion is illustrative and does not constitute investment advice.",
    contact: "Contact",
    rights: "All rights reserved.",
    dba: `${cfg.dba_name} is a d/b/a of ${cfg.entity_name.replace(/\.$/, "")}.`,
    copyright: `© ${cfg.current_year} ${cfg.entity_name.replace(/\.$/, "")}. All rights reserved.`,
  };

  const p = (path) => (lang === "es" ? `/es${path}` : path);

  return (
    <footer role="contentinfo" data-testid="site-footer" className="mt-16 border-t border-slate-200 bg-white text-slate-700">
      <div className="max-w-3xl mx-auto px-4 py-8 text-sm leading-relaxed">
        <div className="font-semibold text-slate-900" data-testid="footer-entity">{t.dba}</div>
        <div className="text-slate-500 text-[13px] mt-0.5" data-testid="footer-copyright">{t.copyright}</div>

        <nav aria-label="Legal" className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[13px]">
          <Link to={p("/privacy")} className="underline text-slate-900" data-testid="footer-privacy">{t.privacy}</Link>
          <Link to={p("/terms")} className="underline text-slate-900" data-testid="footer-terms">{t.terms}</Link>
          <Link to={p("/accessibility")} className="underline text-slate-900" data-testid="footer-accessibility">{t.accessibility}</Link>
          <Link to={p("/find")} className="underline text-slate-900">{t.find}</Link>
          <Link to={p("/provider")} className="underline text-slate-900">{t.provider}</Link>
        </nav>

        <p className="mt-4 text-[13px] text-slate-600 max-w-[70ch]" data-testid="footer-positioning">{t.positioning}</p>
        <p className="mt-3 text-[13px] text-slate-600 max-w-[70ch]" data-testid="footer-compliance">{t.compliance}</p>
        <p className="mt-3 text-[13px] text-slate-600 max-w-[70ch]">{t.securities}</p>

        {cfg.show_trec && (
          <div className="mt-4 p-3 border border-slate-200 rounded text-[13px] leading-6" data-testid="footer-trec" style={{ fontSize: "13px" /* ≥10pt */ }}>
            <div><a href={cfg.trec_iabs_url} rel="noopener" className="underline text-slate-900">Texas Real Estate Commission Information About Brokerage Services</a></div>
            <div className="mt-1"><a href={cfg.trec_cpn_url} rel="noopener" className="underline text-slate-900">Texas Real Estate Commission Consumer Protection Notice</a></div>
            <div className="mt-1">{cfg.trec_broker_name} · License #{cfg.trec_license_number}</div>
          </div>
        )}

        <div className="mt-4 text-[13px] text-slate-600" data-testid="footer-contact">
          <div>{t.contact}: <a className="underline" href={`mailto:${cfg.contact_email}`}>{cfg.contact_email}</a></div>
          {cfg.address_street ? (
            <address className="not-italic mt-1" data-testid="footer-address">
              <div>{cfg.address_street}</div>
              <div>{cfg.address_locality}, {cfg.address_region} {cfg.address_postal_code}</div>
            </address>
          ) : (
            <div>{cfg.business_address}</div>
          )}
        </div>
      </div>
    </footer>
  );
}
